import Box from '@mui/material/Box';
import { t } from 'i18next';
import { useCallback, useContext, useEffect } from 'react';
import { Virtuoso } from 'react-virtuoso';

import DividerWithText from '~components/DividerWithText/DividerWithText';
import { LoadingSpinner } from '~components/Order/ordersDispatchStyledComponents';
import { OrdersAssignedTargetEntity } from '~constants/dispatch';
import ProjectsListCard from '~pages/Sales/Projects/v2/components/ProjectsListCard/ProjectsListCard';
import { useStores } from '~store';

import AssignedOrdersProjectsQueryContext from './AssignedOrdersProjectsQueryContext';

export default function ProjectsList() {
  const { ordersAssignedStore } = useStores();

  const { data, fetchReason, hasMore, fetchMore, clearQueryData } = useContext(
    AssignedOrdersProjectsQueryContext,
  );

  const projects = data ?? [];
  const projectsCount = projects.length;
  const itemCount = projectsCount + 1;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !fetchReason) {
      fetchMore();
    }
  }, [hasMore, fetchReason, fetchMore]);

  const renderProjectItem = useCallback(
    (index: number) => {
      const isLastItem = index === itemCount - 1;
      const project = projects[index];

      if (isLastItem) {
        return (
          <Box sx={{ py: 1.5, px: 6 }}>
            {fetchReason ? (
              <LoadingSpinner isVisible />
            ) : (
              <DividerWithText
                text={
                  projectsCount ? t('sales_v2.all_projects_loaded') : t('common.no_data')
                }
              />
            )}
          </Box>
        );
      }

      return (
        <Box sx={{ pt: 1.5, pb: index === projectsCount - 1 ? 1.5 : 0, px: 6 }}>
          {project ? <ProjectsListCard project={project} /> : null}
        </Box>
      );
    },
    [fetchReason, itemCount, projects, projectsCount],
  );

  useEffect(() => {
    return () => {
      clearQueryData();
      ordersAssignedStore.setTargetEntity(OrdersAssignedTargetEntity.ORDERS);
    };
  }, [clearQueryData]);

  return (
    <Virtuoso
      data={Array.from({ length: itemCount }, (_, index) => index)}
      endReached={handleLoadMore}
      itemContent={renderProjectItem}
      overscan={5}
      style={{ height: '100%' }}
    />
  );
}
